import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Pre-Registration Modal
 *
 * Allows admins to create invitation links for new users
 */
import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FaTimes, FaCopy, FaCheck, FaUserPlus } from 'react-icons/fa';
import { RoleLevel, ROLE_LABELS } from '../types/rbacImproved';
import { apiClient } from '../utils/apiClient';
import { notify } from '../utils/notifications';
import { getAssignableRoleOptions } from '../utils/roleConfig';
const PreRegistrationModal = ({ isOpen, onClose, currentUserRole, initialData }) => {
    const [step, setStep] = useState('form');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [registrationUrl, setRegistrationUrl] = useState('');
    const [expiresAt, setExpiresAt] = useState('');
    const [copied, setCopied] = useState(false);
    const [formData, setFormData] = useState({
        email: initialData?.email || '',
        firstName: initialData?.firstName || '',
        lastName: initialData?.lastName || '',
        businessName: initialData?.businessName || '',
        role: RoleLevel.BASIC_DEALER,
        permissions: []
    });
    // Update form data when initialData changes
    useEffect(() => {
        if (initialData && isOpen) {
            setFormData(prev => ({
                ...prev,
                email: initialData.email || '',
                firstName: initialData.firstName || '',
                lastName: initialData.lastName || '',
                businessName: initialData.businessName || ''
            }));
        }
    }, [initialData, isOpen]);
    const handleInputChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.email || !formData.firstName || !formData.lastName) {
            notify.error('Validation Error', 'Email, first name, and last name are required');
            return;
        }
        setIsSubmitting(true);
        try {
            const response = await apiClient.post('pre-registration-create', formData);
            if (response.ok && response.data?.success && response.data.registrationUrl) {
                setRegistrationUrl(response.data.registrationUrl);
                setExpiresAt(response.data.expiresAt || '');
                setStep('success');
                notify.success('Registration Link Created', 'Copy the link and send it to the user');
            }
            else {
                notify.error('Creation Failed', response.data?.error || 'Failed to create registration link');
            }
        }
        catch (error) {
            notify.error('Error', error.message || 'Failed to create registration link');
        }
        finally {
            setIsSubmitting(false);
        }
    };
    const handleCopyLink = () => {
        navigator.clipboard.writeText(registrationUrl);
        setCopied(true);
        notify.success('Copied!', 'Registration link copied to clipboard');
        setTimeout(() => setCopied(false), 2000);
    };
    const handleClose = () => {
        setStep('form');
        setRegistrationUrl('');
        setExpiresAt('');
        setCopied(false);
        setFormData({
            email: '',
            firstName: '',
            lastName: '',
            businessName: '',
            role: RoleLevel.BASIC_DEALER,
            permissions: []
        });
        onClose();
    };
    if (!isOpen)
        return null;
    const assignableRoles = getAssignableRoleOptions(currentUserRole);
    return (_jsx(ModalOverlay, { onClick: handleClose, children: _jsxs(ModalContainer, { onClick: e => e.stopPropagation(), children: [_jsxs(ModalHeader, { children: [_jsxs(ModalTitle, { children: [_jsx(FaUserPlus, {}), step === 'form' ? 'Create Pre-Registration Link' : 'Registration Link Created'] }), _jsx(CloseButton, { onClick: handleClose, children: _jsx(FaTimes, {}) })] }), step === 'form' ? (_jsxs("form", { onSubmit: handleSubmit, children: [_jsxs(ModalBody, { children: [_jsxs(FormGroup, { children: [_jsx(Label, { children: "Email Address *" }), _jsx(Input, { type: "email", value: formData.email, onChange: e => handleInputChange('email', e.target.value), placeholder: "user@example.com", required: true })] }), _jsxs(FormRow, { children: [_jsxs(FormGroup, { children: [_jsx(Label, { children: "First Name *" }), _jsx(Input, { type: "text", value: formData.firstName, onChange: e => handleInputChange('firstName', e.target.value), placeholder: "John", required: true })] }), _jsxs(FormGroup, { children: [_jsx(Label, { children: "Last Name *" }), _jsx(Input, { type: "text", value: formData.lastName, onChange: e => handleInputChange('lastName', e.target.value), placeholder: "Doe", required: true })] })] }), _jsxs(FormGroup, { children: [_jsx(Label, { children: "Business Name (Optional)" }), _jsx(Input, { type: "text", value: formData.businessName, onChange: e => handleInputChange('businessName', e.target.value), placeholder: "ABC Jewelry Inc." })] }), _jsxs(FormGroup, { children: [_jsx(Label, { children: "Assign Role *" }), _jsx(Select, { value: formData.role, onChange: e => handleInputChange('role', parseInt(e.target.value)), children: assignableRoles.map(role => (_jsx("option", { value: role.level, children: role.label }, role.value))) }), _jsx(HelpText, { children: "User will be assigned this role after completing registration" })] }), _jsxs(InfoBox, { children: [_jsx("strong", { children: "Link Expiration:" }), " 7 days from creation", _jsx("br", {}), _jsx("strong", { children: "What happens next:" }), " User clicks link \u2192 sets password \u2192 auto-logged in"] })] }), _jsxs(ModalFooter, { children: [_jsx(Button, { type: "button", variant: "secondary", onClick: handleClose, children: "Cancel" }), _jsx(Button, { type: "submit", variant: "primary", disabled: isSubmitting, children: isSubmitting ? 'Creating Link...' : 'Create Registration Link' })] })] })) : (_jsxs(_Fragment, { children: [_jsxs(ModalBody, { children: [_jsxs(SuccessBox, { children: [_jsx(FaCheck, { style: { fontSize: '48px', color: '#22c55e', marginBottom: '16px' } }), _jsx("h3", { children: "Registration Link Created!" }), _jsxs("p", { children: ["Copy this link and send it to ", _jsx("strong", { children: formData.email })] })] }), _jsxs(LinkBox, { children: [_jsx(LinkText, { children: registrationUrl }), _jsxs(CopyButton, { onClick: handleCopyLink, children: [copied ? _jsx(FaCheck, {}) : _jsx(FaCopy, {}), copied ? 'Copied!' : 'Copy Link'] })] }), _jsxs(InfoBox, { children: [_jsx("strong", { children: "Expires:" }), " ", new Date(expiresAt).toLocaleString(), _jsx("br", {}), _jsx("strong", { children: "For:" }), " ", formData.firstName, " ", formData.lastName, " (", formData.email, ")", _jsx("br", {}), _jsx("strong", { children: "Role:" }), " ", ROLE_LABELS[formData.role]] })] }), _jsx(ModalFooter, { children: _jsx(Button, { variant: "primary", onClick: handleClose, children: "Done" }) })] }))] }) }));
};
// Styled Components
const ModalOverlay = styled.div `
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
`;
const ModalContainer = styled.div `
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  max-width: 600px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
`;
const ModalHeader = styled.div `
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  border-bottom: 1px solid var(--border-color);
`;
const ModalTitle = styled.h2 `
  margin: 0;
  color: var(--text-primary);
  font-size: 20px;
  display: flex;
  align-items: center;
  gap: 12px;

  svg {
    color: #3b82f6;
  }
`;
const CloseButton = styled.button `
  background: transparent;
  border: none;
  color: var(--text-tertiary);
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;

  &:hover {
    color: var(--text-primary);
  }

  svg {
    font-size: 20px;
  }
`;
const ModalBody = styled.div `
  padding: 24px;
`;
const ModalFooter = styled.div `
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding: 24px;
  border-top: 1px solid var(--border-color);
`;
const FormGroup = styled.div `
  margin-bottom: 20px;
`;
const FormRow = styled.div `
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`;
const Label = styled.label `
  display: block;
  margin-bottom: 8px;
  color: var(--text-primary);
  font-size: 14px;
  font-weight: 500;
`;
const Input = styled.input `
  width: 100%;
  padding: 10px 12px;
  background: var(--bg-tertiary);
  border: 1px solid var(--border-color);
  color: var(--text-primary);
  font-size: 14px;
  border-radius: 0;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #3b82f6;
  }

  &::placeholder {
    color: var(--text-tertiary);
  }
`;
const Select = styled.select `
  width: 100%;
  padding: 10px 12px;
  background: var(--bg-tertiary);
  border: 1px solid var(--border-color);
  color: var(--text-primary);
  font-size: 14px;
  border-radius: 0;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #3b82f6;
  }

  option {
    background: var(--bg-secondary);
    color: var(--text-primary);
  }
`;
const HelpText = styled.p `
  margin: 6px 0 0 0;
  font-size: 12px;
  color: var(--text-tertiary);
`;
const InfoBox = styled.div `
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid #3b82f6;
  padding: 16px;
  margin-top: 16px;
  font-size: 13px;
  color: var(--text-secondary);
  line-height: 1.6;

  strong {
    color: var(--text-primary);
  }
`;
const SuccessBox = styled.div `
  text-align: center;
  margin-bottom: 24px;

  h3 {
    color: var(--text-primary);
    margin: 0 0 8px 0;
    font-size: 18px;
  }

  p {
    color: var(--text-secondary);
    margin: 0;
    font-size: 14px;
  }
`;
const LinkBox = styled.div `
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
`;
const LinkText = styled.div `
  flex: 1;
  padding: 12px;
  background: var(--bg-tertiary);
  border: 1px solid var(--border-color);
  color: var(--text-secondary);
  font-size: 13px;
  word-break: break-all;
  font-family: monospace;
`;
const CopyButton = styled.button `
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background: #3b82f6;
  color: white;
  border: none;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: background 0.2s;
  white-space: nowrap;

  &:hover {
    background: #2563eb;
  }

  svg {
    font-size: 16px;
  }
`;
const Button = styled.button `
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: all 0.2s;

  ${props => props.variant === 'primary' ? `
    background: #3b82f6;
    color: white;
    &:hover { background: #2563eb; }
    &:disabled {
      background: #6b7280;
      cursor: not-allowed;
    }
  ` : `
    background: transparent;
    color: var(--text-primary);
    border: 1px solid var(--border-color);
    &:hover { background: var(--bg-tertiary); }
  `}
`;
export default PreRegistrationModal;
